import React, { useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import BrutalistInput from '../shared/BrutalistInput';
import BrutalistButton2 from '../shared/BrutalistButton2';
import './Segmentacoes.css';
import { criarSegmentacao, atualizarSegmentacao } from '../../../../services/api';

const fieldsBySource = {
  'Clube de Mesa': [
    { label: 'Nível de Fidelidade', value: 'nivel'},
    { label: 'Número de Check-ins', value: 'checkin'},
    { label: 'Dias desde o último Check-in', value: 'ultimo_checkin'},
    { label: 'Dias desde o primeiro Check-in', value: 'primeiro_checkin'},
  ],
  'Atendabot': [
    { label: 'Cliente por dias', value: 'dias_messages'},
    { label: 'Dias desde a última mensagem', value: 'ultima_messages'},
    { label: 'Número de Interações', value: 'pedidos'},
  ]
};

const operators = [
  { value: '>', label: 'Maior que' },
  { value: '>=', label: 'Maior ou igual a' },
  { value: '=', label: 'Igual a' },
  { value: '<', label: 'Menor que' },
  { value: '<=', label: 'Menor ou igual a' },
  { value: '!=', label: 'Diferente de' }
];

const Segmentacoes = ({ segmentacaoEdit, onClose }) => {
  useEffect(() => {
    if (segmentacaoEdit) {
      setSegmentName(segmentacaoEdit.nome || '');
      setConditions(
        (segmentacaoEdit.criterios?.condicoes || []).map((cond, idx) => ({
          source: cond.app_name,
          field: cond.campo,
          operator: cond.operador,
          value: cond.valor,
          connector: idx === 0 ? null : 'E'
        }))
      );
    }
  }, [segmentacaoEdit]);

  const defaultSource = Object.keys(fieldsBySource)[0];
  const fields = fieldsBySource[defaultSource];
  const defaultField = fields[0]?.value || '';
  const defaultOperator = operators[0].value || '';

  const [conditions, setConditions] = useState([
    { source: defaultSource, field: defaultField, operator: defaultOperator, value: '', connector: null }
  ]);
  const [segmentName, setSegmentName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fieldErrors, setFieldErrors] = useState({});

  // Fonte bloqueada pelas próximas condições (igual à primeira condição)
  const lockedSource = conditions[0]?.source || defaultSource;

  // Conector fixo: remoção do toggle E/OU
  // Helper: retorna campos disponíveis sem repetir entre condiçõesa
  const getAvailableFields = (index) => {
    const allFields = fieldsBySource[lockedSource] || [];
    const usedByOthers = new Set(
      conditions.map((c, i) => (i !== index && c.source === lockedSource ? c.field : null)).filter(Boolean)
    );
    const available = allFields.filter((f) => !usedByOthers.has(f.value));
    const current = conditions[index]?.field;
    if (current && !available.find((f) => f.value === current)) {
      const keep = allFields.find((f) => f.value === current);
      if (keep) available.unshift(keep);
    }
    return available;
  };

  const handleConditionChange = (index, key, value) => {
    const newConditions = [...conditions];
    // Bloqueia alteração de source para condições após a primeira
    if (key === 'source' && index > 0) {
      return;
    }

    newConditions[index][key] = value;

    // Se trocar o campo, atualize o operador para o primeiro disponível
    if (key === 'field') {
      if (!value) {
        newConditions[index].operator = '';
      } else {
        newConditions[index].operator = operators[0].value || '';
      }
    }

    // Se trocar o source, atualize field e operator
    if (key === 'source') {
      const fields = fieldsBySource[value] || [];
      // Atualiza a primeira condição
      newConditions[index].field = fields[0]?.value || '';
      newConditions[index].operator = (fields.length ? (operators[0].value || '') : '');

      // Propaga a fonte escolhida para todas as outras condições
      for (let i = 1; i < newConditions.length; i++) {
        newConditions[i].source = value;
        newConditions[i].field = '';
        newConditions[i].operator = '';
      }
    }

    setConditions(newConditions);
    setFieldErrors((prev) => {
      if (!prev.conditions) return prev;
      const next = { ...prev };
      delete next.conditions;
      return next;
    });
  };

  const addCondition = () => {
    setConditions(prev => [
      ...prev,
      {
        source: lockedSource,
        field: '',
        operator: '',
        value: '',
        connector: 'E'
      }
    ]);
  };

  const removeCondition = (index) => {
    if (conditions.length === 1) {
      setSnackbar({ open: true, message: 'A segmentação precisa ter ao menos uma condição.', severity: 'warning' });
      return;
    }
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(newConditions);
  };

  // Função para converter condições do frontend para o formato do backend
  const convertConditionsToBackendFormat = () => {
    if (conditions.length === 0) return null;
    const backendConditions = {
      operador_logico: conditions.length > 1 ? 'AND' : null,
      condicoes: []
    };
    conditions.forEach((condition) => {
      var random_id = 'cond_' + Math.random().toString(36).substr(2, 9);      
      backendConditions.condicoes.push({
        id: random_id,
        app_name: condition.source,
        campo: condition.field,
        operador: condition.operator,
        valor: condition.value
      });
    });

    return backendConditions;
  };

  const handleSaveSegment = async () => {
    const errors = {};
    if (!segmentName) errors.nome = 'Informe um nome para a segmentação.';
    if (conditions.some((cond) => !cond.source || !cond.field || !cond.operator || !cond.value)) {
      errors.conditions = 'Preencha todos os campos das condições.';
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      setSnackbar({
        open: true,
        message: Object.values(errors)[0],
        severity: 'warning',
      });
      return;
    }

    setIsLoading(true);
    try {
      const criterios = convertConditionsToBackendFormat();
      const payload = { nome: segmentName, criterios };
      if (segmentacaoEdit && segmentacaoEdit.id) {
        await atualizarSegmentacao(segmentacaoEdit.id, payload);
        setSnackbar({ open: true, message: 'Segmentação atualizada com sucesso!', severity: 'success' });
      } else {
        await criarSegmentacao(payload);
        setSnackbar({ open: true, message: 'Segmentação criada com sucesso!', severity: 'success' });
      }
      onClose();
    } catch (error) {
      console.error('Erro ao salvar segmentacao:', error);
      setSnackbar({ open: true, message: 'Erro ao salvar segmentação.', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
      <div className="card" style={{ width: '100%', maxWidth: '900px' }}>
        <span className="card__title">Nova Segmentação</span>

        <div className="card__form">
          <BrutalistInput
            id="segmentacao-nome"
            placeholder="Ex: Clientes Inativos VIP"
            label="NOME DA SEGMENTAÇÃO"
            value={segmentName}
            error={fieldErrors.nome}
            onChange={(e) => {
              setSegmentName(e.target.value);
              setFieldErrors((prev) => {
                if (!prev.nome) return prev;
                const next = { ...prev };
                delete next.nome;
                return next;
              });
            }}
          />

          {conditions.map((condition, index) => {
            const availableFields = getAvailableFields(index);

            return (
              <React.Fragment key={index}>
                {index > 0 && (
                  <div style={{ marginBottom: '-0.5rem', marginLeft: '4px' }}>
                    <button
                      type="button"
                      className="brutalist-connector"
                      disabled
                      title="Conectivo fixo: E (AND)"
                    >
                      E
                    </button>
                  </div>
                )}

                <div className="segment-row" style={{ alignItems: 'flex-end' }}>
                  <select
                    className="brutalist-select"
                    value={condition.source}
                    onChange={(e) => handleConditionChange(index, 'source', e.target.value)}
                    disabled={index > 0}
                  >
                    {Object.keys(fieldsBySource).map((src) => (
                      <option key={src} value={src}>{src}</option>
                    ))}
                  </select>

                  <select
                    className="brutalist-select"
                    value={condition.field}
                    onChange={(e) => handleConditionChange(index, 'field', e.target.value)}
                  >
                    <option value="" disabled>
                      Selecione um campo
                    </option>
                    {availableFields.map((field) => (
                      <option key={field.value} value={field.value}>{field.label}</option>
                    ))}
                  </select>

                  <select
                    className="brutalist-select"
                    value={condition.operator}
                    onChange={(e) => handleConditionChange(index, 'operator', e.target.value)}
                    disabled={!condition.field}
                  >
                    {operators.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>

                  <BrutalistInput
                    placeholder="Valor"
                    type="number"
                    pattern="[0-9]*"
                    value={condition.value}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      handleConditionChange(index, 'value', val);
                    }}
                    maxWidth="90px"
                  />

                  <button
                    disabled={conditions.length === 1}
                    onClick={() => removeCondition(index)}
                    style={{
                      padding: '10px',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      backgroundColor: '#ffdddd',
                      color: '#000',
                      border: '3px solid #000',
                      boxShadow: '3px 3px 0 #000',
                      cursor: 'pointer',
                      height: '56px',
                      width: '56px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Excluir condição"
                    aria-label="Excluir condição"
                  >
                    🗑️
                  </button>
                </div>
              </React.Fragment>
            );
          })}

          {fieldErrors.conditions ? (
            <p role="alert" style={{ color: '#b91c1c', fontWeight: 700, fontSize: 12, marginBottom: '1rem' }}>
              {fieldErrors.conditions}
            </p>
          ) : null}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <BrutalistButton2 onClick={addCondition}>
              + ADICIONAR CONDIÇÃO
            </BrutalistButton2>
            <BrutalistButton2 className="orange" onClick={handleSaveSegment} disabled={isLoading}>
              {isLoading ? 'SALVANDO...' : 'SALVAR SEGMENTAÇÃO'}
            </BrutalistButton2>
            <BrutalistButton2 onClick={onClose}>
              VOLTAR
            </BrutalistButton2>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default Segmentacoes;
